import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import Select from 'components/UnifiedSelect';

import Div from 'components/Div';
import FormInput from 'components/FormInput/index';
import PageTitle from 'components/PageTitle/index';
import { getComplaintFilterOptions, getComplaintPeers } from '../api';
import { loadSharedComplaintFilters, saveSharedComplaintFilters } from '../filters/sharedFilters';
import loader from 'assets/images/datahub-loader.svg';

import 'assets/scss/custom/pages/_rankings.scss';
import '../overview/ComplaintsOverview.scss';

type FilterFormValues = {
  company: string;
  brand: string;
  state: string;
  metric: string;
  windowRange: Date[] | null;
};

type PeerComparisonData = {
  filters: {
    brand: string;
    state: string;
    metric: string;
    windowMonths: number;
  };
  summary: {
    brandCount: number;
    brandUnit: string;
    latestAvailableMonth?: string;
    currentStartMonth?: string;
    currentEndMonth?: string;
    lowestPeerLabel: string;
    lowestPeerCount: number;
    lowestPeerNote: string;
    highestPeerLabel: string;
    highestPeerCount: number;
    highestPeerNote: string;
    marketMessage: string;
    marketMessageNote: string;
  };
  chart: Array<{ label: string; value: number; color: string; marker?: string }>;
  insights: Array<{ text: string; kind: string; emphasis: string }>;
};

const peerMetricOptions = [
  { id: 'raw', name: 'Raw count', value: 'raw', disabled: false },
  { id: 'per1m', name: 'Complaints / 1M customers', value: 'per1m', disabled: true },
];
const ALL_VALUE = 'All';

const buildStateChoices = (
  brandValue: string,
  brandsForCompany: Array<{ id: string; name: string; value: string }>,
  brandStatesMap: Record<string, Array<{ id: string; name: string; value: string }>>
) => {
  if (brandValue !== ALL_VALUE) {
    const states = brandStatesMap[brandValue] || [];
    return states.some((item) => item.value === ALL_VALUE) ? states : [{ id: ALL_VALUE, name: ALL_VALUE, value: ALL_VALUE }, ...states];
  }

  const merged = brandsForCompany
    .flatMap((brand) => brandStatesMap[brand.value] || [])
    .reduce((acc, row) => {
      if (!acc.some((item) => item.value === row.value)) {
        acc.push({ id: row.value, name: row.name, value: row.value });
      }
      return acc;
    }, [] as Array<{ id: string; name: string; value: string }>)
    .sort((a, b) => a.name.localeCompare(b.name));

  return [{ id: ALL_VALUE, name: ALL_VALUE, value: ALL_VALUE }, ...merged];
};

const getMonthStart = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
const getMonthRange = (monthCount: number, endDate = new Date()): [Date, Date] => {
  const end = getMonthStart(endDate);
  const start = new Date(end.getFullYear(), end.getMonth() - (monthCount - 1), 1);
  return [start, end];
};
const toMonthParam = (value: unknown) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-01`;
  }
  return null;
};
const formatMonthLabel = (value?: Date | null) => {
  if (!value) return '';
  return value.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

const renderTitleWithRange = (title: string) => {
  const parts = title.split(/(\s\([^)]+\))/g).filter(Boolean);
  if (parts.length <= 1) return <span>{title}</span>;

  return <span>{parts.map((part, index) => (/^\s\([^)]+\)$/.test(part) ? <span key={index} className="complaints-overview__panel-title-range">{part}</span> : <span key={index}>{part}</span>))}</span>;
};

const SummaryCard = ({
  eyebrow,
  primary,
  secondary,
  infoTooltip,
  loading,
}: {
  eyebrow: string;
  primary: string;
  secondary?: string;
  infoTooltip?: ReactNode;
  loading?: boolean;
}) => (
  <div className="complaints-overview__summary-card h-100">
    <div className="complaints-overview__eyebrow complaints-overview__summary-eyebrow d-flex align-items-center gap-2">
      <span>{eyebrow}</span>
      {infoTooltip ? (
        <OverlayTrigger placement="top" overlay={<Tooltip id={`peers-summary-tooltip-${eyebrow.replace(/\s+/g, '-').toLowerCase()}`}>{infoTooltip}</Tooltip>}>
          <i className="mdi mdi-information-outline" role="button" aria-label={`${eyebrow} details`} />
        </OverlayTrigger>
      ) : null}
    </div>
    <div className="d-flex align-items-center gap-2 flex-wrap complaints-overview__summary-content complaints-peers__summary-content">
      {loading ? (
        <div className="d-flex align-items-center justify-content-center w-100" style={{ minHeight: 44 }}>
          <img src={loader} height={36} alt="Loading" />
        </div>
      ) : (
        <>
          <div className="complaints-overview__hero-number">{primary}</div>
          {secondary ? (
            <div className={`complaints-overview__hero-note ${secondary.toLowerCase().includes('above') || secondary.trim().startsWith('+') ? 'complaints-overview__trend-up' : secondary.toLowerCase().includes('below') ? 'complaints-overview__trend-down' : 'complaints-overview__trend-flat'}`}>
              {(secondary.toLowerCase().includes('above') || secondary.trim().startsWith('+')) ? <i className="mdi mdi-arrow-up" /> : secondary.toLowerCase().includes('below') ? <i className="mdi mdi-arrow-down" /> : null} {secondary}
            </div>
          ) : null}
        </>
      )}
    </div>
  </div>
);

const FilterLoader = () => (
  <div className="mb-4 ranking-filters">
    <div className="row g-3 ranking-section align-items-end">
      {[0, 1, 2, 3].map((key) => (
        <div key={key} className="col-12 col-md-6 col-xl">
          <div className="complaints-overview__placeholder-line complaints-overview__placeholder-line--sm mb-2" />
          <div className="complaints-overview__placeholder-line complaints-overview__placeholder-line--md complaints-overview__filter-skeleton" />
        </div>
      ))}
      <div className="col-12 col-xl-auto complaints-overview__actions-col">
        <div className="d-flex justify-content-xl-end gap-2 complaints-overview__actions">
          <div className="complaints-overview__placeholder-line complaints-overview__button-skeleton" />
          <div className="complaints-overview__placeholder-line complaints-overview__button-skeleton" />
        </div>
      </div>
    </div>
  </div>
);

const ChartPanel = ({
  title,
  data,
  loading,
  infoTooltip,
}: {
  title: string;
  data: PeerComparisonData['chart'];
  loading: boolean;
  infoTooltip?: ReactNode;
}) => (
  <div className="complaints-overview__panel h-100">
    <div className="complaints-overview__panel-title d-flex align-items-center gap-2">
      {renderTitleWithRange(title)}
      {infoTooltip ? (
        <OverlayTrigger placement="top" overlay={<Tooltip id={`peers-chart-tooltip-${title.replace(/\s+/g, '-').toLowerCase()}`}>{infoTooltip}</Tooltip>}>
          <i className="mdi mdi-information-outline" role="button" aria-label="Chart details" />
        </OverlayTrigger>
      ) : null}
    </div>
    {loading ? (
      <div className="d-flex align-items-center justify-content-center" style={{ height: 220 }}>
        <img src={loader} height={44} alt="Loading" />
      </div>
    ) : data.length === 0 ? (
      <div className="d-flex align-items-center justify-content-center text-muted" style={{ height: 220 }}>
        Peer chart data is not available for the selected filters.
      </div>
    ) : (
      <div className="complaints-overview__chart-shell">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 28, right: 8, left: -24, bottom: 0 }}>
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              interval={0}
              height={58}
              tick={({ x, y, payload }) => {
                const row = data.find((item) => item.label === payload.value);
                const marker = row?.marker || '';
                return (
                  <g transform={`translate(${x},${y})`}>
                    <text x={0} y={0} dy={16} textAnchor="middle" fill="#6c7a92" fontSize={12}>
                      {payload.value}
                    </text>
                    {marker ? (
                      <text x={0} y={0} dy={30} textAnchor="middle" fill="#9aa6bd" fontSize={11}>
                        {marker}
                      </text>
                    ) : null}
                  </g>
                );
              }}
            />
            <YAxis hide />
            <Bar dataKey="value" radius={[10, 10, 0, 0]} maxBarSize={110}>
              <LabelList dataKey="value" position="top" style={{ fill: '#213047', fontSize: 12, fontWeight: 600 }} />
              {data.map((entry) => (
                <Cell key={entry.label} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    )}
  </div>
);

const TakeawayPanel = ({
  title,
  items,
  loading,
  infoTooltip,
}: {
  title: string;
  items: Array<{ text: string; kind: string; emphasis: string }>;
  loading: boolean;
  infoTooltip?: ReactNode;
}) => (
  <div className="complaints-overview__panel h-100">
    <div className="complaints-overview__panel-title d-flex align-items-center gap-2">
      {renderTitleWithRange(title)}
      {infoTooltip ? (
        <OverlayTrigger placement="top" overlay={<Tooltip id={`peers-takeaway-tooltip-${title.replace(/\s+/g, '-').toLowerCase()}`}>{infoTooltip}</Tooltip>}>
          <i className="mdi mdi-information-outline" role="button" aria-label="Panel details" />
        </OverlayTrigger>
      ) : null}
    </div>
    {loading ? (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 180 }}>
        <img src={loader} height={44} alt="Loading" />
      </div>
    ) : items.length === 0 ? (
      <div className="d-flex align-items-center justify-content-center text-muted" style={{ minHeight: 180 }}>
        No benchmark insight available for current selection.
      </div>
    ) : (
      <div className="complaints-overview__takeaway-list">
        {items.map((item) => (
          <div key={item.text} className="complaints-overview__takeaway-item">
            <p className="complaints-overview__takeaway-text">{item.text}</p>
            <div className="complaints-overview__takeaway-meta">
              <span className="badge badge-outline-secondary">{item.kind}</span>
              <span className="complaints-overview__takeaway-emphasis">{item.emphasis}</span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const buildEmptyPeerData = (filters: { brand: string; state: string; metric: string }): PeerComparisonData => ({
  filters: { brand: filters.brand, state: filters.state, metric: filters.metric, windowMonths: 12 },
  summary: {
    brandCount: 0,
    brandUnit: 'raw',
    lowestPeerLabel: '-',
    lowestPeerCount: 0,
    lowestPeerNote: 'No data in selected window',
    highestPeerLabel: '-',
    highestPeerCount: 0,
    highestPeerNote: 'No data in selected window',
    marketMessage: 'No data',
    marketMessageNote: 'Try another state or window',
  },
  chart: [],
  insights: [{ text: 'No complaint data found for selected brand/state/window.', kind: 'Info', emphasis: 'Review filters' }],
});

const ComplaintsPeers = () => {
  const initialFilters = {
    company: '',
    brand: '',
    state: '',
    metric: peerMetricOptions[0].value,
    windowRange: getMonthRange(12),
  };

  const { register, handleSubmit, reset, setValue, watch, control } = useForm<FilterFormValues>({ defaultValues: initialFilters });
  const [defaultFilters, setDefaultFilters] = useState<FilterFormValues>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<FilterFormValues>(initialFilters);
  const [companyChoices, setCompanyChoices] = useState<Array<{ id: string; name: string; value: string }>>([]);
  const [brandChoices, setBrandChoices] = useState<Array<{ id: string; name: string; value: string }>>([]);
  const [companyNameById, setCompanyNameById] = useState<Record<string, string>>({});
  const [brandNameById, setBrandNameById] = useState<Record<string, string>>({});
  const [companyBrandsMap, setCompanyBrandsMap] = useState<Record<string, Array<{ id: string; name: string; value: string }>>>({});
  const [stateChoices, setStateChoices] = useState<Array<{ id: string; name: string; value: string }>>([]);
  const [brandStatesMap, setBrandStatesMap] = useState<Record<string, Array<{ id: string; name: string; value: string }>>>({});
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [hasPeersData, setHasPeersData] = useState(false);
  const showContentLoading = filtersLoading || dataLoading || !hasPeersData;
  const [data, setData] = useState<PeerComparisonData>({
    filters: { brand: '', state: '', metric: 'raw', windowMonths: 12 },
    summary: { brandCount: 0, brandUnit: 'raw', lowestPeerLabel: '-', lowestPeerCount: 0, lowestPeerNote: '', highestPeerLabel: '-', highestPeerCount: 0, highestPeerNote: '', marketMessage: '-', marketMessageNote: '' },
    chart: [],
    insights: [],
  });

  const selectedFilterPills = useMemo(
    () => [
      ['Company', companyNameById[appliedFilters.company] || companyChoices.find((item) => item.value === appliedFilters.company)?.name || appliedFilters.company],
      ['Brand', appliedFilters.brand === ALL_VALUE ? ALL_VALUE : (brandNameById[appliedFilters.brand] || brandChoices.find((item) => item.value === appliedFilters.brand)?.name || appliedFilters.brand)],
      ['State', stateChoices.find((item) => item.value === appliedFilters.state)?.name || appliedFilters.state],
      ['Window', Array.isArray(appliedFilters.windowRange) && appliedFilters.windowRange.length === 2 ? `${formatMonthLabel(appliedFilters.windowRange[0])}-${formatMonthLabel(appliedFilters.windowRange[1])}` : ''],
    ],
    [appliedFilters, companyChoices, brandChoices, stateChoices, companyNameById, brandNameById]
  );

  const selectedBrandDisplayName = useMemo(
    () => (appliedFilters.brand === ALL_VALUE ? ALL_VALUE : (brandNameById[appliedFilters.brand] || brandChoices.find((item) => item.value === appliedFilters.brand)?.name || '-')),
    [brandChoices, appliedFilters.brand, brandNameById]
  );

  const activeWindowLabel = 'Window';

  const formatMonth = (value?: string) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  };

  const currentRangeLabel = useMemo(() => {
    const start = formatMonth(data.summary.currentStartMonth);
    const end = formatMonth(data.summary.currentEndMonth);
    return start !== '-' && end !== '-' ? `${start}-${end}` : '';
  }, [data.summary.currentStartMonth, data.summary.currentEndMonth]);

  const selectedStateLabel = useMemo(
    () => stateChoices.find((item) => item.value === appliedFilters.state)?.name || appliedFilters.state || '-',
    [stateChoices, appliedFilters.state]
  );

  const summaryCountTooltip = 'Total raw complaint count for the selected brand, in the selected state and window.';
  const peerOneIsBelow = (data.summary.lowestPeerNote || '').toLowerCase().includes('below');
  const peerTwoIsBelow = (data.summary.highestPeerNote || '').toLowerCase().includes('below');
  const peerOneIsAbove = (data.summary.lowestPeerNote || '').toLowerCase().includes('above');
  const peerTwoIsAbove = (data.summary.highestPeerNote || '').toLowerCase().includes('above');

  const lowestPeerTooltip =
    peerOneIsBelow && peerTwoIsBelow
      ? 'Peer #1 shows the nearest lower peer, by complaint count, relative to the selected brand.'
      : peerOneIsAbove && peerTwoIsAbove
        ? 'Peer #1 shows the nearest higher peer, by complaint count, relative to the selected brand.'
        : 'Peer #1 shows one side of the nearest peer benchmark, by complaint count, relative to the selected brand.';

  const highestPeerTooltip =
    peerOneIsBelow && peerTwoIsBelow
      ? 'Peer #2 shows the next lower peer, by complaint count, relative to the selected brand.'
      : peerOneIsAbove && peerTwoIsAbove
        ? 'Peer #2 shows the next higher peer, by complaint count, relative to the selected brand.'
        : 'Peer #2 shows the opposite-side nearest peer benchmark, by complaint count, relative to the selected brand.';
  const marketMessageTooltip = 'Overall peer position signal derived from selected brand count versus the peer set.';
  const rawCountChartTooltip = `Raw complaint counts across peer brands for ${selectedStateLabel} in ${activeWindowLabel}${currentRangeLabel ? ` (${currentRangeLabel})` : ''}.`;
  const benchmarkTooltip = 'Narrative insights generated from selected brand peer ranking.';

  const selectedCompanyValue = watch('company');
  const selectedCompanyOption = useMemo(
    () => companyChoices.find((option) => option.value === selectedCompanyValue) || null,
    [companyChoices, selectedCompanyValue]
  );
  const selectedBrandValue = watch('brand');
  const selectedBrandOption = useMemo(
    () => brandChoices.find((option) => option.value === selectedBrandValue) || null,
    [brandChoices, selectedBrandValue]
  );
  const selectedStateValue = watch('state');
  const selectedStateOption = useMemo(
    () => stateChoices.find((option) => option.value === selectedStateValue) || null,
    [stateChoices, selectedStateValue]
  );

  useEffect(() => {
    document.title = 'DataHub - Complaints - Peer Comparison';

    const loadFilters = async () => {
      try {
        const filterOptions = await getComplaintFilterOptions();
        const companies = (filterOptions?.companies || []).map((item: { companyId: string; companyName: string }) => ({ id: item.companyId, name: item.companyName, value: item.companyId }));
        const nextCompanyNameById = companies.reduce((acc, item) => {
          acc[item.value] = item.name;
          return acc;
        }, {} as Record<string, string>);
        const companyBrands = (filterOptions?.companyBrands || []) as Array<{ companyId: string; brandId: string; brandName: string }>;
        const nextBrandNameById = companyBrands.reduce((acc, row) => {
          if (row?.brandId && row?.brandName && !acc[row.brandId]) {
            acc[row.brandId] = row.brandName;
          }
          return acc;
        }, {} as Record<string, string>);
        const nextCompanyBrandsMap = companyBrands.reduce((acc, row) => {
          if (!row?.companyId || !row?.brandId) return acc;
          if (!acc[row.companyId]) acc[row.companyId] = [];
          if (!acc[row.companyId].some((item) => item.value === row.brandId)) {
            acc[row.companyId].push({ id: row.brandId, name: row.brandName, value: row.brandId });
          }
          return acc;
        }, {} as Record<string, Array<{ id: string; name: string; value: string }>>);
        Object.values(nextCompanyBrandsMap).forEach((items) => items.sort((a, b) => a.name.localeCompare(b.name)));
        const defaultCompany = companies[0]?.value || '';
        const stored = loadSharedComplaintFilters();
        const selectedCompany = companies.some((item) => item.value === stored.company) ? (stored.company as string) : defaultCompany;
        const brands = nextCompanyBrandsMap[selectedCompany] || [];
        const brandsWithAll = [{ id: ALL_VALUE, name: ALL_VALUE, value: ALL_VALUE }, ...brands];
        const brandStates = (filterOptions?.brandStates || []) as Array<{ brandId: string; brandName: string; stateCode: string }>;
        const nextBrandStatesMap = brandStates.reduce((acc, row) => {
          if (!row?.brandId || !row?.stateCode) return acc;
          if (!acc[row.brandId]) acc[row.brandId] = [];
          if (!acc[row.brandId].some((item) => item.value === row.stateCode)) {
            acc[row.brandId].push({ id: row.stateCode, name: row.stateCode, value: row.stateCode });
          }
          return acc;
        }, {} as Record<string, Array<{ id: string; name: string; value: string }>>);
        Object.values(nextBrandStatesMap).forEach((states) => states.sort((a, b) => a.name.localeCompare(b.name)));

        const defaultBrand = brands[0]?.value || '';
        const selectedBrand = brandsWithAll.some((item) => item.value === stored.brand) ? (stored.brand as string) : ALL_VALUE;
        const statesWithAll = buildStateChoices(selectedBrand, brands, nextBrandStatesMap);
        const selectedState = statesWithAll.some((item) => item.value === stored.state) ? (stored.state as string) : ALL_VALUE;
        const resetDefaults = {
          company: selectedCompany,
          brand: ALL_VALUE,
          state: ALL_VALUE,
          metric: peerMetricOptions[0].value,
          windowRange: getMonthRange(12),
        };
        const nextDefaults = {
          ...resetDefaults,
          brand: selectedBrand,
          state: selectedState,
          metric: peerMetricOptions.some((item) => item.value === stored.metric) ? (stored.metric as string) : peerMetricOptions[0].value,
          windowRange: stored.windowRange || resetDefaults.windowRange,
        };

        setCompanyChoices(companies);
        setCompanyNameById(nextCompanyNameById);
        setBrandNameById(nextBrandNameById);
        setCompanyBrandsMap(nextCompanyBrandsMap);
        setBrandStatesMap(nextBrandStatesMap);
        setBrandChoices(brandsWithAll);
        setStateChoices(statesWithAll);
        setDefaultFilters(resetDefaults);
        reset(nextDefaults);
        setAppliedFilters(nextDefaults);
      } catch (error) {
        console.error('Failed to load complaints peers filters', error);
        setHasPeersData(true);
      } finally {
        setFiltersLoading(false);
      }
    };

    loadFilters();
  }, []);

  useEffect(() => {
    if (!appliedFilters.company) {
      setData(buildEmptyPeerData(appliedFilters));
      if (!filtersLoading) {
        setHasPeersData(true);
      }
      setDataLoading(false);
      return;
    }

    setDataLoading(true);

    const loadPeers = async () => {
      try {
        const selectedBrandName = appliedFilters.brand === ALL_VALUE
          ? 'All Brands'
          : (brandChoices.find((item) => item.value === appliedFilters.brand)?.name || '');
        const response = await getComplaintPeers({
          complaintCompanyId: appliedFilters.company,
          brandId: appliedFilters.brand === ALL_VALUE ? null : appliedFilters.brand,
          brandName: selectedBrandName,
          stateCode: appliedFilters.state === ALL_VALUE ? null : appliedFilters.state,
            metric: appliedFilters.metric,
            fromMonth: toMonthParam(Array.isArray(appliedFilters.windowRange) ? appliedFilters.windowRange[0] : null),
            toMonth: toMonthParam(Array.isArray(appliedFilters.windowRange) ? appliedFilters.windowRange[1] : null),
          });

        setData(response);
        setHasPeersData(true);
      } catch (error) {
        console.error('Failed to load complaints peers', error);
        setData(buildEmptyPeerData(appliedFilters));
        setHasPeersData(true);

        const errorText = String((error as { message?: string })?.message || '').toLowerCase();
        const noDataForState = errorText.includes('no complaint data') || errorText.includes('no data');
        if (noDataForState) {
          const remainingStates = stateChoices.filter((item) => item.value !== appliedFilters.state);
          if (remainingStates.length > 0) {
            const fallbackState = remainingStates[0].value;
            setStateChoices(remainingStates);
            setValue('state', fallbackState);
            setAppliedFilters((current) => ({ ...current, state: fallbackState }));
          }
        }
      } finally {
        setDataLoading(false);
      }
    };

    loadPeers();
  }, [appliedFilters, filtersLoading]);

  const onDeleteFilter = (label: string) => {
    const fieldMap: Record<string, keyof FilterFormValues> = {
      Company: 'company',
      Brand: 'brand',
      State: 'state',
      Window: 'windowRange',
    };
    const field = fieldMap[label];
    if (!field) return;
    setValue(field, defaultFilters[field]);
    const next = { ...appliedFilters, [field]: defaultFilters[field] };
    setAppliedFilters(next);
    saveSharedComplaintFilters(next);
  };

  return (
    <div className="complaints-overview complaints-peers">
      <Div>
        <PageTitle title="Peer Comparison" />
      </Div>

      <Card className="ranking-module complaints-overview__module border-0">
        <Card.Body>
          {filtersLoading ? (
            <FilterLoader />
          ) : (
            <Form onSubmit={handleSubmit(async (values) => {
              const brands = companyBrandsMap[values.company] || [];
              const brandsWithAll = [{ id: ALL_VALUE, name: ALL_VALUE, value: ALL_VALUE }, ...brands];
              const nextBrand = brandsWithAll.some((item) => item.value === values.brand) ? values.brand : ALL_VALUE;
              const statesWithAll = buildStateChoices(nextBrand, brands, brandStatesMap);
              const nextValues = {
                ...values,
                brand: nextBrand,
                state: statesWithAll.some((item) => item.value === values.state) ? values.state : ALL_VALUE,
              };
              setBrandChoices(brandsWithAll);
              setStateChoices(statesWithAll);
              reset(nextValues);
              setAppliedFilters(nextValues);
              saveSharedComplaintFilters(nextValues);
            })} className="ranking-filters mb-4">
              <Div className="mb-2 ranking-section">
                <Row className="g-3 align-items-end">
                  <Col xl md={6}>
                    <Form.Group className="complaints-overview__select">
                      <Form.Label className="form-label geography-label">Company</Form.Label>
                      <Select
                        className="react-select"
                        classNamePrefix="react-select"
                        options={companyChoices.map((option) => ({ value: option.value, label: option.name }))}
                        value={selectedCompanyOption ? { value: selectedCompanyOption.value, label: selectedCompanyOption.name } : null}
                        onChange={(option: { value: string; label: string } | null) => {
                          const companyValue = option?.value || '';
                          const brands = companyBrandsMap[companyValue] || [];
                          const brandsWithAll = [{ id: ALL_VALUE, name: ALL_VALUE, value: ALL_VALUE }, ...brands];
                          const nextBrand = ALL_VALUE;
                          const states = buildStateChoices(nextBrand, brands, brandStatesMap);
                          setValue('company', companyValue);
                          setValue('brand', nextBrand);
                          setValue('state', ALL_VALUE);
                          setBrandChoices(brandsWithAll);
                          setStateChoices(states);
                        }}
                        isSearchable
                        isClearable={false}
                      />
                      <input type="hidden" {...register('company')} />
                    </Form.Group>
                  </Col>
                  <Col xl md={6}>
                    <Form.Group className="complaints-overview__select">
                      <Form.Label className="form-label geography-label">Brand</Form.Label>
                      <Select
                        className="react-select"
                        classNamePrefix="react-select"
                        options={brandChoices.map((option) => ({ value: option.value, label: option.name }))}
                        value={selectedBrandOption ? { value: selectedBrandOption.value, label: selectedBrandOption.name } : null}
                        onChange={(option: { value: string; label: string } | null) => {
                          const brandValue = option?.value || '';
                          const currentCompany = watch('company');
                          const companyBrands = companyBrandsMap[currentCompany] || [];
                          const statesWithAll = buildStateChoices(brandValue, companyBrands, brandStatesMap);
                          setValue('brand', brandValue);
                          setValue('state', ALL_VALUE);
                          setStateChoices(statesWithAll);
                        }}
                        isSearchable
                        isClearable={false}
                      />
                      <input type="hidden" {...register('brand')} />
                    </Form.Group>
                  </Col>
                  <Col xl md={6}>
                    <Form.Group className="complaints-overview__select">
                      <Form.Label className="form-label geography-label">State</Form.Label>
                      <Select
                        className="react-select"
                        classNamePrefix="react-select"
                        options={stateChoices.map((option) => ({ value: option.value, label: option.name }))}
                        value={selectedStateOption ? { value: selectedStateOption.value, label: selectedStateOption.name } : null}
                        onChange={(option: { value: string; label: string } | null) => setValue('state', option?.value || '')}
                        isSearchable
                        isClearable={false}
                      />
                      <input type="hidden" {...register('state')} />
                    </Form.Group>
                  </Col>
                  <input type="hidden" {...register('metric')} value={peerMetricOptions[0].value} />
                  <Col xl md={6}>
                    <FormInput
                      type="date"
                      label="Window"
                      name="windowRange"
                      control={control}
                      errors={{}}
                      range
                      onlyMonthPicker
                      readOnly
                      labelClassName="form-label geography-label"
                      placeholder="mm/yyyy - mm/yyyy"
                      validation={{
                        validate: (value: unknown) => Array.isArray(value) && value.length === 2 ? true : 'Window From and To are required',
                      }}
                    />
                  </Col>
                  <Col xl="auto" xs={12} className="complaints-overview__actions-col">
                    <Div className="d-flex justify-content-xl-end gap-2 complaints-overview__actions">
                      <Button type="button" variant="primary" onClick={() => { reset(defaultFilters); setAppliedFilters(defaultFilters); saveSharedComplaintFilters(defaultFilters); }}>Reset</Button>
                      <Button type="submit" variant="primary">Apply</Button>
                    </Div>
                  </Col>
                </Row>
              </Div>
              <Div className="mb-2 ranking-section">
                <Div className="d-flex flex-wrap gap-2 complaints-overview__pill-row">
                  {selectedFilterPills.map(([label, value]) => (
                    <Badge key={label} className="mx-1" bg="secondary">
                      {label}: {value}
                      <i className="mdi mdi-close" onClick={() => onDeleteFilter(label)} style={{ cursor: 'pointer', marginLeft: '5px' }} role="button" aria-label={`Remove ${label}`}></i>
                    </Badge>
                  ))}
                </Div>
              </Div>
            </Form>
          )}

          <Row className="g-3 mb-3">
            <Col xl={3} md={6}><SummaryCard eyebrow={`${selectedBrandDisplayName.toUpperCase()} Count`} primary={`${data.summary.brandCount}`} secondary="raw" infoTooltip={summaryCountTooltip} loading={showContentLoading} /></Col>
            <Col xl={3} md={6}><SummaryCard eyebrow={showContentLoading ? 'PEER #1 COUNT' : `${data.summary.lowestPeerLabel.toUpperCase()} Count`} primary={`${data.summary.lowestPeerCount}`} secondary={data.summary.lowestPeerNote} infoTooltip={lowestPeerTooltip} loading={showContentLoading} /></Col>
            <Col xl={3} md={6}><SummaryCard eyebrow={showContentLoading ? 'PEER #2 COUNT' : `${data.summary.highestPeerLabel.toUpperCase()} Count`} primary={`${data.summary.highestPeerCount}`} secondary={data.summary.highestPeerNote} infoTooltip={highestPeerTooltip} loading={showContentLoading} /></Col>
            <Col xl={3} md={6}><SummaryCard eyebrow="Market Message" primary={data.summary.marketMessage} secondary={data.summary.marketMessageNote} infoTooltip={marketMessageTooltip} loading={showContentLoading} /></Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col xl={12}><ChartPanel title={`Complaints raw count`} data={data.chart} loading={showContentLoading} infoTooltip={rawCountChartTooltip} /></Col>
          </Row>

          <Row className="g-3">
            <Col xl={12}><TakeawayPanel title="Benchmark insight panel" items={data.insights} loading={showContentLoading} infoTooltip={benchmarkTooltip} /></Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ComplaintsPeers;
